import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./css/login.css";
import { apiUrl } from "./config/api";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get("oauth");
    const bootstrapCode = params.get("bootstrapCode");

    if (oauthStatus === "google" && bootstrapCode) {
      const returnTo = params.get("returnTo") || "/app/home";
      window.history.replaceState({}, document.title, window.location.pathname);
      completeGoogleLogin(bootstrapCode, returnTo);
      return;
    }

    if (oauthStatus === "error") {
      setError(params.get("message") || "No se pudo iniciar sesión con Google.");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const session = JSON.parse(localStorage.getItem("sessionData"));
    if (
      session &&
      session.sessionKey &&
      session.username &&
      session.expiry &&
      Date.now() < session.expiry
    ) {
      navigate("/app/home");
    }
    // The OAuth bootstrap should only run from the callback URL on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const completeLogin = async (result, returnTo = "/app/home") => {
    const storedSessionData = JSON.parse(localStorage.getItem("sessionData"));
    const sessionData = {
      sessionKey: result.key || storedSessionData?.sessionKey,
      username: result.username || username,
      expiry: Date.now() + result.ttl,
      location: result.location,
    };

    localStorage.setItem("sessionData", JSON.stringify(sessionData));
    setError("Login successful! Redirecting...");

    const modules = { modules: await fetchUserModules(sessionData) };
    if (modules) {
      localStorage.setItem("userModules", JSON.stringify(modules));
    }

    const userNameResult = await getUserName(sessionData);
    if (userNameResult && userNameResult.name) {
      localStorage.setItem("realName", userNameResult.name);
    }

    const locations = await fetchAvailableLocations(sessionData);
    const availableLocations = {};
    for (let loc of locations) {
      availableLocations[loc.id] = loc.name;
    }
    const storingLocationsIDs = sessionData.location || [];
    const storingLocations = {};
    for (let loc of storingLocationsIDs) {
      storingLocations[loc] = availableLocations[loc];
    }

    localStorage.setItem("availableLocations", JSON.stringify(storingLocations));

    const saleUnits = await fetchAvailableSaleUnits(sessionData);
    if (saleUnits) {
      const storingSaleUnits = {};
      for (let unit of saleUnits) {
        storingSaleUnits[unit.id] = unit.sale_unit;
      }
      localStorage.setItem("availableSaleUnits", JSON.stringify(storingSaleUnits));
    }

    const categories = await fetchAvailableCategories(sessionData);
    if (categories) {
      const storingCategories = {};
      for (let cat of categories) {
        if (storingCategories[cat.category] === undefined) {
          storingCategories[cat.category] = {
            subcategory: []
          };
        }
        storingCategories[cat.category].subcategory.push(cat.subcategory);
      }
      localStorage.setItem("availableCategories", JSON.stringify(storingCategories));
    }

    window.history.replaceState({}, document.title, window.location.pathname);
    setTimeout(() => {
      navigate(returnTo.startsWith("/app/") ? returnTo : "/app/home");
    }, 500);
  };

  const completeGoogleLogin = async (bootstrapCode, returnTo = "/app/home") => {
    try {
      const response = await fetch(apiUrl("/auth/bootstrap"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bootstrapCode }),
      });
      const result = await response.json();

      if (response.ok && result.success) {
        await completeLogin(result, returnTo);
      } else {
        setError(result.message || "No se pudo iniciar sesión con Google.");
      }
    } catch (err) {
      console.error("Google login bootstrap error:", err);
      setError("Server error. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Login attempt with username:", username);

    if (!username || !password) {
      setError("Please enter username and password.");
      return;
    }

    try {
      console.log("Attempting to log in...");
      const response = await fetch(apiUrl("/login"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const result = await response.json();

      if (response.ok && result.success) {
        await completeLogin(result);
      } else {
        setError(result.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Server error. Please try again.");
    }
  };

  const handleGoogleLogin = () => {
    const authUrl = new URL(apiUrl("/auth/google"), window.location.origin);
    authUrl.searchParams.set("appOrigin", window.location.origin);
    authUrl.searchParams.set("returnTo", "/");
    window.location.assign(authUrl.toString());
  };

  async function fetchUserModules(sessionData) {
    try {
      const response = await fetch(apiUrl("/user/modules"), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });
  
      if (response.ok) {
        const result = await response.json();
        return result.modules; // returns array directly
      } else if (response.status === 401) {
        console.error("Session expired or invalid");
        localStorage.removeItem("sessionData");
        return null;
      } else {
        console.error("Failed to fetch user modules:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user modules:", error);
      return null;
    }
  }

  async function fetchAvailableSaleUnits(sessionData) {
    try {
      const response = await fetch(apiUrl("/sales/sale-units"), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });
  
      const data = await response.json();
  
      if (!response.ok || !data.success) {
        console.error("Failed to fetch sale units:", data.message || "Unknown error");
        return null;
      }
  
      return data.saleUnits;
    } catch (error) {
      console.error("Error during fetchAvailableSaleUnits:", error);
      return null;
    }
  }
  
  
  async function fetchAvailableLocations(sessionData) {
    try {
      const response = await fetch(apiUrl("/locations/all"), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });
  
      if (response.ok) {
        const result = await response.json();
        return result.locations; // array of { id, name }
      } else {
        console.error("Failed to fetch locations:", response.status);
        return [];
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      return [];
    }
  }

  async function fetchAvailableCategories(sessionData) {
    try {
      const response = await fetch(apiUrl("/products/categories/all"), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.categories; // array of { id, name }
      } else {
        console.error("Failed to fetch categories:", response.status);
        return [];
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
  }
  

  async function getUserName(sessionData) {
    try {
      const response = await fetch(apiUrl("/user/name"), {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        return await response.json(); // expects { name: "John Doe" }
      } else if (response.status === 401) {
        console.error("Session expired or invalid");
        localStorage.removeItem("sessionData");
        return null;
      } else {
        console.error("Failed to fetch user name:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user name:", error);
      return null;
    }
  }

  return (
    <main className="login-page">
      <section className="login-page__hero" aria-labelledby="login-title">
        <img src="/images/logowp.png" className="login-page__logo" alt="Kakao Apps" />
        <div className="login-page__hero-copy">
          <div className="login-page__badge">Kakao Apps</div>
          <h1 id="login-title">Login</h1>
          <p>Utiliza tus credenciales para acceder al sitio</p>
        </div>
      </section>

      <section className="login-container" aria-label="Formulario de acceso">
        <form className="signin-form" id="login-form" onSubmit={handleSubmit} autoComplete="off">
          <div className="signin-form__fields">
            <label className="signin-field" htmlFor="username">
              <span>Username</span>
              <input
                type="text"
                id="username"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>

            <label className="signin-field" htmlFor="password">
              <span>Password</span>
              <div className="signin-field__input">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="signin-field__toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="signin-field__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M3.3 2.3 21.7 20.7l-1.4 1.4-3.1-3.1A12.7 12.7 0 0 1 12 20C6.5 20 2.1 16.3.6 12c.7-2 2-3.9 3.7-5.4L1.9 4.2l1.4-1.4ZM12 4c5.5 0 9.9 3.7 11.4 8-1 2.6-2.8 4.9-5.1 6.4l-2.1-2.1a4.5 4.5 0 0 0-6.1-6.1L8.4 8.6A9.7 9.7 0 0 1 12 8c2.2 0 4 1.8 4 4 0 .6-.1 1.2-.4 1.7l2.5 2.5A10 10 0 0 0 21.2 12C19.9 8.8 16.2 6 12 6a9.9 9.9 0 0 0-4.5 1.1l-1.7-1.7A12.1 12.1 0 0 1 12 4Z"
                    />
                  </svg>
                ) : (
                  <svg className="signin-field__icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M12 5c5.5 0 9.9 3.7 11.4 8-1.5 4.3-5.9 8-11.4 8S2.1 17.3.6 13C2.1 8.7 6.5 5 12 5Zm0 2C7.4 7 3.7 9.6 2.3 13 3.7 16.4 7.4 19 12 19s8.3-2.6 9.7-6c-1.4-3.4-5.1-6-9.7-6Zm0 2.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Zm0 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"
                    />
                  </svg>
                )}
                <span className="signin-field__toggle-text">
                  {showPassword ? "Hide password" : "View password"}
                </span>
              </button>
            </label>
          </div>

          <button className="primary-button" type="submit">Login</button>

          <p className="signin-form__helper">
            Forgot your password?{" "}
            <Link
              className="signin-form__link"
              to="/forgot-password"
            >
              Click here
            </Link>
            .
          </p>

          <button
            className="google-button"
            type="button"
            onClick={handleGoogleLogin}
          >
            <span className="google-button__icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" role="img">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.34 1.54 7.79 2.83l5.67-5.67C34.02 3.6 29.39 1.5 24 1.5 14.61 1.5 6.52 6.9 2.64 14.8l6.96 5.4C11.3 13.7 17.18 9.5 24 9.5Z" />
                <path fill="#4285F4" d="M46.5 24c0-1.57-.14-3.08-.41-4.55H24v9.07h12.65c-.55 2.94-2.2 5.43-4.68 7.09l7.57 5.86C43.89 37.56 46.5 31.22 46.5 24Z" />
                <path fill="#FBBC05" d="M9.6 28.2a14.6 14.6 0 0 1 0-8.4l-6.96-5.4a23 23 0 0 0 0 19.2l6.96-5.4Z" />
                <path fill="#34A853" d="M24 46.5c6.39 0 11.76-2.12 15.68-5.77l-7.57-5.86c-2.1 1.41-4.8 2.26-8.11 2.26-6.82 0-12.7-4.2-14.4-10.1l-6.96 5.4C6.52 41.1 14.61 46.5 24 46.5Z" />
              </svg>
            </span>
            Continue with Google
          </button>
        </form>

        <div
          id="error-message"
          style={{ color: error.includes("success") ? "green" : "red" }}
        >
          {error}
        </div>
      </section>
    </main>
  );
}

export default Login;
